/**
 * /admin/onboarding — Setup-guide step overrides per user.
 *
 * Wraps the per-user `GET/POST/DELETE /api/admin/onboarding/steps/{user_id}`
 * endpoints in a focused UI: pick a user → see every persona-specific
 * step → toggle each one done/undone with a single click.
 *
 * Use cases:
 *   - Unstick a user from a step the auto-detector is mis-grading.
 *   - Pre-complete steps for a power user who's done them via another
 *     workflow (e.g., legacy import).
 *   - Admin testing the platform — silence the nag on test accounts.
 *
 * Steps catalog is duplicated from `OnboardingChecklist.jsx` to keep
 * the source of truth small. If you add a new persona / step there,
 * mirror it here.
 */
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { ArrowLeftIcon as ArrowLeft, MagnifyingGlassIcon as MagnifyingGlass, CheckCircleIcon as CheckCircle, CircleIcon as Circle, TruckIcon as Truck, HouseIcon as House, HeartStraightIcon as HeartStraight, StorefrontIcon as Storefront, ShieldStarIcon as ShieldStar, UserIcon as UserIcon } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

// Mirror of OnboardingChecklist.jsx STEPS — keep in sync.
const STEPS = {
  breeder: [
    { id: "first-dog",     label: "Register your first dog",         hint: "Photos + breed + DOB. Takes ~2 minutes." },
    { id: "kennel-page",   label: "Set up your kennel storefront",   hint: "About + photos + verified-breeder badge." },
    { id: "first-listing", label: "List a dog for sale",             hint: "Or list a litter. Lands in front of every buyer browsing." },
  ],
  buyer: [
    { id: "preferences",   label: "Tell us what you're looking for", hint: "Breed, size, location. We'll alert you when matching dogs are listed." },
    { id: "watchlist",     label: "Save a breeder or litter you like", hint: "Heart icon on any kennel page or litter card." },
    { id: "first-inquiry", label: "Send your first inquiry",         hint: "Ask a breeder about their dogs — they typically reply within 24h." },
  ],
  transporter: [
    { id: "stripe-connect", label: "Connect Stripe payouts",         hint: "Required before they can take bids. Takes 5 minutes." },
    { id: "service-area",   label: "Set service area",               hint: "Which states do they cover? Bid invitations only route to matches." },
    { id: "first-bid",      label: "Submit first bid",               hint: "Open bid invitations appear on the dashboard." },
  ],
  dog_owner: [
    { id: "first-log",     label: "Log first meal or walk",          hint: "Tap the + button at the bottom of any screen on mobile." },
    { id: "vet-contact",   label: "Add vet's contact",               hint: "Saves time when a sitter needs to call them in an emergency." },
    { id: "share-sitter",  label: "Generate a sitter link",          hint: "A read-only view of recent care — perfect for boarding." },
  ],
};

// Personas (and icons) the admin can toggle steps for.
const PERSONA_META = {
  breeder:     { label: "Breeder",     icon: House,         tone: "#8A6A00" },
  buyer:       { label: "Buyer",       icon: Storefront,    tone: "#3B82F6" },
  transporter: { label: "Transporter", icon: Truck,         tone: "#C1272D" },
  dog_owner:   { label: "Dog Owner",   icon: HeartStraight, tone: "#0EA5A0" },
};

export default function AdminOnboarding() {
  const [query, setQuery] = useState("");
  const [users, setUsers] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null); // user object
  const [overrides, setOverrides] = useState([]); // {guide, step_id, completed_at, completed_by}
  const [loadingOverrides, setLoadingOverrides] = useState(false);
  const [pending, setPending] = useState(null); // step_id while toggling

  // Debounced user search.
  useEffect(() => {
    const t = setTimeout(() => {
      if (!query.trim()) { setUsers([]); return; }
      setSearching(true);
      api.get(`/admin/data/users?q=${encodeURIComponent(query.trim())}&limit=25`)
        .then((r) => setUsers(r.data?.items || []))
        .catch(() => setUsers([]))
        .finally(() => setSearching(false));
    }, 250);
    return () => clearTimeout(t);
  }, [query]);

  const loadOverrides = useCallback(async (userId) => {
    if (!userId) return;
    setLoadingOverrides(true);
    try {
      const r = await api.get(`/admin/onboarding/steps/${userId}`);
      setOverrides(r.data?.overrides || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load overrides.");
      setOverrides([]);
    } finally {
      setLoadingOverrides(false);
    }
  }, []);

  useEffect(() => {
    if (selected?.id) loadOverrides(selected.id);
  }, [selected, loadOverrides]);

  const completedSet = useMemo(() => {
    return new Set(
      overrides.filter((o) => o.guide === "welcome").map((o) => o.step_id),
    );
  }, [overrides]);

  const toggle = async (stepId, currentlyDone) => {
    if (!selected) return;
    setPending(stepId);
    try {
      if (currentlyDone) {
        await api.delete(`/admin/onboarding/steps/${selected.id}/welcome/${stepId}`);
        setOverrides((prev) => prev.filter((o) => !(o.guide === "welcome" && o.step_id === stepId)));
        toast.success(`Unmarked '${stepId}'`);
      } else {
        const r = await api.post(`/admin/onboarding/steps/${selected.id}`, { guide: "welcome", step_id: stepId });
        setOverrides((prev) => [...prev.filter((o) => !(o.guide === "welcome" && o.step_id === stepId)), r.data]);
        toast.success(`Marked '${stepId}' done`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toggle failed.");
    } finally {
      setPending(null);
    }
  };

  const skipAll = async (persona) => {
    if (!selected) return;
    const open = STEPS[persona].filter((s) => !completedSet.has(s.id));
    if (open.length === 0) return;
    await Promise.all(
      open.map((s) =>
        api.post(`/admin/onboarding/steps/${selected.id}`, { guide: "welcome", step_id: s.id }).catch(() => null)
      )
    );
    toast.success(`Marked ${open.length} ${persona} step${open.length === 1 ? "" : "s"} done`);
    loadOverrides(selected.id);
  };

  const resetAll = async (persona) => {
    if (!selected) return;
    const done = STEPS[persona].filter((s) => completedSet.has(s.id));
    if (done.length === 0) return;
    await Promise.all(
      done.map((s) =>
        api.delete(`/admin/onboarding/steps/${selected.id}/welcome/${s.id}`).catch(() => null)
      )
    );
    toast.success(`Cleared ${done.length} ${persona} override${done.length === 1 ? "" : "s"}`);
    loadOverrides(selected.id);
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8" data-testid="admin-onboarding-page">
      <Link to="/admin" className="inline-flex items-center gap-1.5 text-[#6B6B6B] hover:text-ink text-sm mb-4">
        <ArrowLeft size={14} weight="bold" /> Back to admin
      </Link>
      <header className="mb-6">
        <h1 className="font-spectral text-4xl sm:text-5xl tracking-tight text-ink">Setup-guide overrides</h1>
        <p className="text-[#6B6B6B] mt-2 max-w-2xl">
          Pick a user, then tick any step in their persona's onboarding checklist to mark it done — or untick to return it to auto-graded state. Useful for unsticking a user the auto-detector mis-grades, pre-completing steps for power users, or silencing the nag on a test account.
        </p>
      </header>

      {/* User picker */}
      <section className="bg-white border border-[#E5E5E5] rounded-2xl p-5 mb-6">
        <label className="block text-[11px] font-bold uppercase tracking-[0.18em] text-[#6B6B6B] mb-2">Find a user</label>
        <div className="relative">
          <MagnifyingGlass size={16} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by email or name…"
            className="pl-9 h-11"
            data-testid="admin-onboarding-search"
          />
        </div>
        {query.trim() && users.length > 0 && !selected && (
          <div className="mt-3 max-h-72 overflow-y-auto rounded-xl border border-[#E5E5E5] divide-y divide-[#F0F0F0]">
            {users.map((u) => (
              <button
                key={u.id}
                type="button"
                onClick={() => { setSelected(u); setQuery(""); setUsers([]); }}
                className="w-full text-left px-3 py-2.5 hover:bg-[#FAFAFA] flex items-center gap-3"
                data-testid={`admin-onboarding-user-${u.id}`}
              >
                <UserIcon size={16} weight="duotone" className="text-[#9CA3AF] flex-shrink-0" />
                <span className="flex-1 min-w-0">
                  <span className="block text-[13px] font-semibold text-ink truncate">{u.name || u.email}</span>
                  <span className="block text-[11px] text-[#6B6B6B] truncate">{u.email}</span>
                </span>
                {u.plan && <Badge className="bg-[#FBF7EC] text-gold-dark border-0 text-[10px]">{u.plan}</Badge>}
                {u.role === "admin" && <Badge className="bg-ink text-gold border-0 text-[10px]">Admin</Badge>}
              </button>
            ))}
          </div>
        )}
        {searching && <p className="text-[12px] text-[#6B6B6B] mt-2">Searching…</p>}
        {query.trim() && !searching && users.length === 0 && (
          <p className="text-[12px] text-[#6B6B6B] mt-2">No users match.</p>
        )}
      </section>

      {selected && (
        <section className="bg-white border border-[#E5E5E5] rounded-2xl p-5 sm:p-6" data-testid="admin-onboarding-detail">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-5 pb-4 border-b border-[#F0F0F0]">
            <div className="min-w-0">
              <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#6B6B6B] mb-1">Editing overrides for</p>
              <p className="font-spectral text-2xl text-ink leading-tight">{selected.name || selected.email}</p>
              <p className="text-[12px] text-[#6B6B6B]">{selected.email}</p>
            </div>
            <Button
              variant="ghost"
              onClick={() => { setSelected(null); setOverrides([]); }}
              className="text-[12px] rounded-full"
              data-testid="admin-onboarding-clear-user"
            >
              Pick another user
            </Button>
          </div>

          {loadingOverrides ? (
            <p className="text-sm text-[#6B6B6B] py-8 text-center">Loading…</p>
          ) : (
            <div className="space-y-8">
              {Object.keys(PERSONA_META).map((persona) => {
                const meta = PERSONA_META[persona];
                const PersonaIcon = meta.icon;
                const steps = STEPS[persona];
                const doneCount = steps.filter((s) => completedSet.has(s.id)).length;
                return (
                  <div key={persona} data-testid={`admin-onboarding-persona-${persona}`}>
                    <div className="flex items-center gap-2 mb-3">
                      <span className="inline-flex items-center justify-center h-8 w-8 rounded-full flex-shrink-0" style={{ background: `${meta.tone}15`, color: meta.tone }}>
                        <PersonaIcon size={16} weight="duotone" />
                      </span>
                      <h3 className="font-spectral text-lg tracking-tight text-ink">{meta.label} checklist</h3>
                      <Badge className="bg-[#FAFAFA] text-[#6B6B6B] border border-[#E5E5E5] text-[10px] ml-1">
                        {doneCount}/{steps.length} overridden
                      </Badge>
                      <div className="ml-auto flex items-center gap-1">
                        {doneCount < steps.length && (
                          <button
                            type="button"
                            onClick={() => skipAll(persona)}
                            className="text-[10px] font-semibold text-[#6B6B6B] hover:text-[#15803D] uppercase tracking-wider px-2 py-1 rounded-full hover:bg-[#DCFCE7]"
                            data-testid={`admin-onboarding-skipall-${persona}`}
                          >
                            Mark all done
                          </button>
                        )}
                        {doneCount > 0 && (
                          <button
                            type="button"
                            onClick={() => resetAll(persona)}
                            className="text-[10px] font-semibold text-[#6B6B6B] hover:text-crimson uppercase tracking-wider px-2 py-1 rounded-full hover:bg-[#FEF3C7]"
                            data-testid={`admin-onboarding-resetall-${persona}`}
                          >
                            Clear all
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="space-y-2">
                      {steps.map((s) => {
                        const done = completedSet.has(s.id);
                        return (
                          <button
                            key={s.id}
                            type="button"
                            onClick={() => toggle(s.id, done)}
                            disabled={pending === s.id}
                            data-testid={`admin-onboarding-toggle-${persona}-${s.id}`}
                            className={`w-full text-left flex items-center gap-3 rounded-xl border px-3 py-2.5 transition-colors ${
                              done
                                ? "border-[#DCFCE7] bg-[#F0FDF4] hover:border-[#BBF7D0]"
                                : "border-[#E5E5E5] bg-white hover:border-ink/40"
                            } disabled:opacity-50`}
                          >
                            <span className="flex-shrink-0">
                              {done ? (
                                <CheckCircle size={20} weight="fill" className="text-[#15803D]" />
                              ) : (
                                <Circle size={20} weight="duotone" className="text-[#9CA3AF]" />
                              )}
                            </span>
                            <span className="flex-1 min-w-0">
                              <span className={`block text-[13px] font-semibold ${done ? "line-through text-[#6B6B6B]" : "text-ink"}`}>
                                {s.label}
                              </span>
                              <span className="block text-[11px] text-[#6B6B6B] mt-0.5">{s.hint}</span>
                            </span>
                            <span className="text-[10px] font-semibold text-[#6B6B6B] uppercase tracking-wider flex-shrink-0">
                              {done ? "Tap to undo" : "Tap to mark done"}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      )}

      {!selected && (
        <div className="bg-[#FAFAFA] border border-[#E5E5E5] rounded-2xl p-8 text-center">
          <UserIcon size={42} weight="duotone" className="mx-auto text-[#9CA3AF] mb-3" />
          <p className="font-spectral text-xl text-ink">Pick a user above to get started</p>
          <p className="text-sm text-[#6B6B6B] mt-2 max-w-md mx-auto">
            Search by email or name. Once you select someone, you'll see every step in every persona's onboarding checklist with a quick tap-to-toggle.
          </p>
        </div>
      )}
    </div>
  );
}
